import React from 'react';
import { X, Plus, Minus, ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CartDrawer = ({ 
  isOpen, 
  onClose, 
  cart, 
  cartTotal, 
  updateCartItemQuantity, 
  removeFromCart 
}) => {
  const navigate = useNavigate();
  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      toast.success('Item removed from cart');
    } else {
      updateCartItemQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
    toast.success('Item removed from cart');
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    // Close cart drawer and navigate to checkout
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
          onClick={onClose}
          data-testid="cart-overlay"
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 flex items-center" data-testid="cart-title">
            <ShoppingBag className="w-5 h-5 mr-2" />
            Shopping Cart ({cart.length})
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-cart-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-cart">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Start shopping to add items to your cart</p>
              <Button onClick={onClose} className="bg-brand-purple hover:bg-brand-purple-dark text-white">
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="cart-items">
              {cart.map((item) => (
                <div key={item.id} className="cart-item" data-testid={`cart-item-${item.id}`}>
                  <div className="flex items-center space-x-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover"
                        data-testid={`cart-item-image-${item.id}`}
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate" data-testid={`cart-item-name-${item.id}`}>
                        {item.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-testid={`cart-item-brand-${item.id}`}>
                        {item.brand}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-600" data-testid={`cart-item-size-${item.id}`}>
                          Size: {item.size}
                        </span>
                        <span className="text-sm text-gray-600" data-testid={`cart-item-color-${item.id}`}>
                          {item.color}
                        </span>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      data-testid={`remove-item-button-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Quantity and Price */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="p-1 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                          data-testid={`decrease-quantity-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        
                        <span 
                          className="w-12 text-center font-medium"
                          data-testid={`item-quantity-${item.id}`}
                        >
                          {item.quantity}
                        </span>
                        
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.maxStock}
                          className={`p-1 rounded-full border transition-colors ${
                            item.quantity >= item.maxStock 
                              ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' 
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`increase-quantity-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      {item.maxStock && (
                        <span className="text-xs text-gray-500 text-center">
                          {item.quantity >= item.maxStock ? 'Max stock reached' : `${item.maxStock} available`}
                        </span>
                      )}
                    </div>

                    <div className="text-lg font-bold text-gray-900" data-testid={`item-total-${item.id}`}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-medium text-gray-900">Total:</span>
              <span className="text-2xl font-bold text-gray-900" data-testid="cart-total">
                ${cartTotal.toFixed(2)}
              </span>
            </div>
            
            <Button
              onClick={handleCheckout}
              className="w-full bg-brand-purple hover:bg-brand-purple-dark text-white py-3 text-lg font-semibold"
              data-testid="checkout-button"
            >
              One Click from Your Pair
            </Button>
            
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full mt-2 text-gray-600 hover:text-gray-900"
              data-testid="continue-shopping-button"
            >
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;